import api from "../../../utils/api";

// ============================================================================
// Universo de compañías (#1070 / #1071, épica #1074)
// ============================================================================
// Una compañía de la plataforma (`companies`, /reseller-admin/companies) y su
// Empresa del CRM (`crm_companies`) están enlazadas 1:1; su negocio en
// `crm_deals` sigue el estado de la compañía. Lo usan los dos listados para
// mostrar el mismo universo:
//   • GET    /companies/status-options      estados de ESTE proyecto con su clase
//   • GET    /companies/commercial-summary  estado + Empresa del CRM + negocio
//   • PUT    /company/crm-link?id=          vincular ({ crmCompanyId } | { create })
//   • DELETE /company/crm-link?id=          desvincular (no si es Facturable)
//   • GET    /crm/companiesUniverse         resumen + totales (solo plataforma)
// ============================================================================

export type BillingClass = "billable" | "not_billable" | "paused" | "ended" | "unknown";

export interface PlatformCommercial {
  id: string;
  name: string | null;
  statusId: string | null;
  statusName: string | null;
  billingClass: BillingClass;
  crmCompanyId: string | null;
  crmCompanyName: string | null;
  crmStage: string | null;
  dealId: string | null;
  dealTitle: string | null;
  dealStage: string | null;
  /** Facturable sin Empresa del CRM (dato previo a la regla): hay que vincularla. */
  needsLink: boolean;
}

export interface UniverseTotals {
  platform: number;
  linked: number;
  unlinked: number;
  billable: number;
  notBillable: number;
  paused: number;
  ended: number;
  needsLink: number;
}

export interface CompanyStatusOption {
  id: string;
  name: string;
  billingClass: BillingClass;
}

export const BILLING_META: Record<BillingClass, { label: string; tone: string }> = {
  billable: { label: "Facturable", tone: "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" },
  not_billable: { label: "No facturable", tone: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  paused: { label: "Baja temporal", tone: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  ended: { label: "Baja total", tone: "bg-red-500/15 text-red-500" },
  unknown: { label: "Sin estado", tone: "bg-gray-400/15 text-gray-500" },
};

/** Etiqueta del estado: el nombre del catálogo si lo hay, si no el de su clase. */
export const billingLabel = (c: Pick<PlatformCommercial, "statusName" | "billingClass">) =>
  c.statusName || BILLING_META[c.billingClass]?.label || "Sin estado";

/** Mensaje del backend (ClientError) o uno genérico. */
export function apiErrorMessage(error: unknown, fallback: string): string {
  const e = error as { response?: { data?: { message?: string; meta?: { message?: string }; error?: string } } };
  const d = e?.response?.data;
  return d?.message || d?.meta?.message || (typeof d?.error === "string" ? d.error : "") || fallback;
}

export async function fetchCompanyStatusOptions(): Promise<CompanyStatusOption[]> {
  const res = await api.get("/companies/status-options");
  const data = res.data?.data?.data;
  return Array.isArray(data) ? data : [];
}

export async function fetchCommercialSummary(): Promise<PlatformCommercial[]> {
  const res = await api.get("/companies/commercial-summary");
  const data = res.data?.data?.data;
  return Array.isArray(data) ? data : [];
}

/** Universo con totales. null si quien llama no es de plataforma (403). */
export async function fetchCompaniesUniverse(): Promise<{ platform: PlatformCommercial[]; totals: UniverseTotals } | null> {
  try {
    const res = await api.get("/crm/companiesUniverse");
    const data = res.data?.data?.data;
    return data && Array.isArray(data.platform) ? data : null;
  } catch (error) {
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status === 403 || status === 401) return null;
    throw error;
  }
}

export async function linkCompanyToCrm(
  platformCompanyId: string,
  target: { crmCompanyId: string } | { create: true },
): Promise<void> {
  await api.put("/company/crm-link", target, { params: { id: platformCompanyId } });
}

export async function unlinkCompanyFromCrm(platformCompanyId: string): Promise<void> {
  await api.delete("/company/crm-link", { params: { id: platformCompanyId } });
}
